import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Radio, Row, Select } from "antd";

const { TextArea } = Input;
import { DeleteFilled } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { ADJUSTMENT } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: ADJUSTMENT,
  BranchId: null,
  DepartmentId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const [addDetailInOut, setAddDetailInOut] = useState("Out");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(false);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  const fetchInitialData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: ADJUSTMENT,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {
        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        const normalizedDetails = (detailsData || []).map((detail) => ({
          ...detail,
          InOut: detail.InOut ?? "Out",
        }));
        setTransactionDetails(normalizedDetails);
      }

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: [],
        });
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: ADJUSTMENT,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }
    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      if (data.name === "BranchId" || data.name === "DepartmentId") {
        if (!updated.BranchId || !updated.DepartmentId) {
          setSupportingTable((prev) => ({
            ...prev,
            Items: [],
          }));
          setSelectedItemForAdd(null);
          itemsFetchedRef.current = null;
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName + " ( " + (item?.IssuanceUnit ?? item?.UnitName ?? "") + " )";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockIU = (item) => item?.StockIU ?? item?.Stock ?? 0;

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);
    const inOut = addDetailInOut;

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      // For "Out" - validate quantity against available stock
      if (inOut === "Out") {
        const stockIU = selectedItemForAdd.StockIU ?? 0;
        if (qty > stockIU) {
          message.error(`Quantity cannot exceed available stock. Available stock: ${stockIU.toFixed(2)} ${selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? ""}`);
          return;
        }
      }

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: 0,
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        StockPU: selectedItemForAdd.StockPU ?? 0,
        IsEnable: true,
        InOut: inOut,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    if (field === "QtyIU") {
      value = clampQtyRateValue(value);
      const qty = value;
      const currentDetail = transactionDetails[index];
      const inOut = currentDetail.InOut ?? "Out";

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      if (inOut === "Out") {
        const stockIU = currentDetail.StockIU ?? 0;
        const currentQtyIU = currentDetail.QtyIU ?? 0;
        const availableStock = stockIU + currentQtyIU;
        if (qty > availableStock) {
          message.error(`Quantity cannot exceed available stock. Available stock: ${availableStock.toFixed(2)} ${currentDetail.IssuanceUnit ?? ""}`);
          return;
        }
      }
    }

    const updatedDetails = [...transactionDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };
    if (field === "QtyIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
    }
    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "StockAdjustment")) {
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    if (isSubmitFlag) {
      const stockInvalidItems = transactionDetails.filter((detail) =>
        detail.InOut === "Out" &&
        detail.StockIU !== undefined &&
        parseFloat(detail.QtyIU) > parseFloat(detail.StockIU)
      );

      if (stockInvalidItems.length > 0) {
        const invalidItemDetails = stockInvalidItems
          .map((item) =>
            `${item.ItemName || "Unknown Item"} (Quantity: ${item.QtyIU?.toFixed(2)}, Available Stock: ${item.StockIU?.toFixed(2)} ${item.IssuanceUnit || ""})`
          )
          .join("; ");
        message.error(
          `Quantity exceeds available stock for the following items: ${invalidItemDetails}. Please adjust the quantities before submitting.`
        );
        return;
      }
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: ADJUSTMENT,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: 0,
      Tax: 0,
      Discount: 0,
      Amount: 0,
      VendorId: null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
        InOut: detail.InOut ?? "Out",
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/stockadjustment");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} adjustment`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      { title: "Stock Quantity", dataIndex: "StockIU", key: "StockIU" },
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => {
          const stockIU = record.StockIU ?? 0;
          const currentQtyIU = record.QtyIU ?? 0;
          const inOut = record.InOut ?? "Out";
          const availableStock = inOut === "Out" ? stockIU + currentQtyIU : undefined;
          return (
            <Input
              type="number"
              min={0}
              value={record.QtyIU}
              onChange={(e) =>
                handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
              }
              style={{ width: "100%" }}
              readOnly={formFields.IsSubmit}
              min={0}
              max={availableStock}
              step={0.01}
            />
          );
        },
      },
      {
        title: "In/Out",
        dataIndex: "InOut",
        key: "InOut",
        width: 100,
        render: (_, record, index) => {
          if (formFields.IsSubmit) {
            return record.InOut ?? "Out";
          }
          return (
            <Radio.Group
              value={record.InOut ?? "Out"}
              onChange={(e) => handleDetailChange(index, "InOut", e.target.value)}
              size="small"
            >
              <Radio.Button value="In">In</Radio.Button>
              <Radio.Button value="Out">Out</Radio.Button>
            </Radio.Group>
          );
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              />
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 500 };

  const formPanel = (
    <Fragment>
      <Col xs={24} sm={12} md={4}>
        <FormTextField
          colSpan={24}
          label="Transaction No"
          name="TransactionNo"
          size={INPUT_SIZE}
          value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
          readOnly={true}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <label style={labelStyle}>
          Transaction Date <span style={{ color: "#ff4d4f" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <DatePicker
            style={{ width: "100%" }}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
        ) : (
          <FormTextField
            name="TransactionDate"
            size={INPUT_SIZE}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
            readOnly={true}
          />
        )}
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Branches || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="BranchId"
          label="Branch"
          value={formFields.BranchId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Departments || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="DepartmentId"
          label="Department"
          value={formFields.DepartmentId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col span={24}>
        <label style={labelStyle}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description (optional)"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <FormTextField
          colSpan={24}
          label="Total Items"
          name="TotalItems"
          size={INPUT_SIZE}
          value={transactionDetails.length}
          readOnly={true}
          type="text"
          isNumber="true"
        />
      </Col>
    </Fragment>
  );

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add item to adjustment
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Available Stock</label>
              <Input
                readOnly
                disabled
                size="middle"
                value={selectedItemForAdd ? getStockIU(selectedItemForAdd).toFixed(2) : "—"}
                style={{
                  background: "#fff",
                  cursor: "default",
                  color: "#595959",
                }}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity (IU)</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => {
                  const value = e.target.value;
                  setAddDetailQty(value);
                  if (selectedItemForAdd && value && addDetailInOut === "Out") {
                    const qty = parseFloat(value) || 0;
                    const stockIU = getStockIU(selectedItemForAdd);
                    if (qty > stockIU) {
                      message.warning(`Quantity exceeds available stock (${stockIU.toFixed(2)})`, 2);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddDetail();
                  }
                }}
                min={0}
                max={addDetailInOut === "Out" && selectedItemForAdd ? getStockIU(selectedItemForAdd) : undefined}
                step={0.01}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <label style={labelStyle}>In / Out</label>
              <Radio.Group
                value={addDetailInOut}
                onChange={(e) => setAddDetailInOut(e.target.value)}
                size="middle"
                style={{ width: "100%" }}
              >
                <Radio.Button value="In">In</Radio.Button>
                <Radio.Button value="Out">Out</Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Adjustment" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Adjustment" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/stockadjustment"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "StockAdjustment", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/stockadjustment")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
